import { View, StyleSheet, Platform } from "react-native";
import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { useToast } from "../../../contexts/Toast/ToastProvider";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import { colors } from "../../../styles/design-system";
import { Plus } from "lucide-react";
import {
  ConnectorInstanceStatus,
  ConnectorType,
  ExternalTokenHealthStatus,
  UserExternalAuthStatus,
  useDisconnectUserAccountMutation,
  useGetConnectorCatalogQuery,
  useGetEnabledConnectorsQuery,
  useGetUserConnectionsQuery,
  ConnectorDefinition,
} from "@axio/graphql/generated/graphql";
import handleOpenLink from "../../../utils/handleOpenLink";
import resolveConnectorPermissions from "../../../utils/integrations/permissionsResolver";
import { connectorAllowsUserRole } from "../../../utils/integrations/roleAccess";
import { formatSubmittedDate } from "../../../utils/date";
import { ConnectorReauthenticationBanner } from "../../../components/settings/Connectors/ConnectorReauthenticationBanner";
import { ConnectedServiceAccordion } from "../../../components/settings/Connectors/ConnectedServiceAccordion/ConnectedServiceAccordion";
import { ServiceConnectorCard } from "../../../components/settings/Connectors/ServiceConnectorCard/ServiceConnectorCard";
import { DisconnectServiceModal } from "../../../components/settings/Connectors/DisconnectServiceModal";
import { useAuth } from "../../../hooks/useAuth";

const isConnectorType = (value: string): value is ConnectorType =>
  Object.values(ConnectorType).includes(value as ConnectorType);

const isConnection = <T,>(value: T | null): value is T => value !== null;

const getStatusTone = (status?: UserExternalAuthStatus | null) => {
  switch (status) {
    case UserExternalAuthStatus.Active:
      return "success" as const;
    case UserExternalAuthStatus.ReauthRequired:
      return "error" as const;
    default:
      return "neutral" as const;
  }
};

const isActionableHealthStatus = (healthStatus?: ExternalTokenHealthStatus | null) => {
  return (
    healthStatus === ExternalTokenHealthStatus.Expired ||
    healthStatus === ExternalTokenHealthStatus.Revoked ||
    healthStatus === ExternalTokenHealthStatus.Unknown
  );
};

const hasUnresolvedConnectionIssue = (
  status?: UserExternalAuthStatus | null,
  healthStatus?: ExternalTokenHealthStatus | null
) => {
  if (status && status !== UserExternalAuthStatus.Active) {
    return true;
  }

  return isActionableHealthStatus(healthStatus);
};

const connectionNeedsReconnect = (
  status?: UserExternalAuthStatus | null,
  healthStatus?: ExternalTokenHealthStatus | null
) => {
  return (
    status === UserExternalAuthStatus.ReauthRequired ||
    status === UserExternalAuthStatus.Error ||
    status === UserExternalAuthStatus.Revoked ||
    isActionableHealthStatus(healthStatus)
  );
};

const getConnectionStatusTone = (
  status?: UserExternalAuthStatus | null,
  healthStatus?: ExternalTokenHealthStatus | null
) => {
  if (
    status === UserExternalAuthStatus.ReauthRequired ||
    status === UserExternalAuthStatus.Error ||
    status === UserExternalAuthStatus.Revoked ||
    healthStatus === ExternalTokenHealthStatus.Expired ||
    healthStatus === ExternalTokenHealthStatus.Revoked ||
    healthStatus === ExternalTokenHealthStatus.Unknown
  ) {
    return "error" as const;
  }

  return getStatusTone(status);
};

const getStatusLabel = (status?: UserExternalAuthStatus | null, healthStatus?: ExternalTokenHealthStatus | null) => {
  if (status === UserExternalAuthStatus.Active && !isActionableHealthStatus(healthStatus)) return "Active";
  if (healthStatus === ExternalTokenHealthStatus.Expired || status === UserExternalAuthStatus.ReauthRequired) {
    return "Expired";
  }
  if (healthStatus === ExternalTokenHealthStatus.Revoked || status === UserExternalAuthStatus.Revoked) return "Revoked";
  if (healthStatus === ExternalTokenHealthStatus.Unknown || status === UserExternalAuthStatus.Error) {
    return "Needs attention";
  }
  return "Unknown";
};

const clearOAuthQueryParams = () => {
  if (typeof window === "undefined" || !window.history?.replaceState) {
    return;
  }

  const url = new URL(window.location.href);
  url.searchParams.delete("connectorType");
  url.searchParams.delete("connectionId");
  url.searchParams.delete("reconnectUserConnectionId");
  url.searchParams.delete("amp;connectorType");
  url.searchParams.delete("amp;reconnectUserConnectionId");
  url.searchParams.delete("initialTab");

  window.history.replaceState({}, "", url.toString());
};

const isPersonalConnector = (connector: ConnectorDefinition) => {
  return connector?.savedConfiguration?.authorizationUrl;
};

export const ChatIntegration = () => {
  const { showToast } = useToast();
  const { userInfo } = useAuth();
  const [disconnectAccount] = useDisconnectUserAccountMutation();
  const [serviceToDisconnect, setServiceToDisconnect] = useState<{
    id: string;
    name: string;
    email: string;
  } | null>(null);
  const [connectingConnectorId, setConnectingConnectorId] = useState<string | null>(null);
  const [reconnectingConnectionId, setReconnectingConnectionId] = useState<string | null>(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [dismissedIssueSignature, setDismissedIssueSignature] = useState<string | null>(null);

  const returnUrl = (() => {
    if (Platform.OS !== "web" || typeof window === "undefined") return null;
    const url = new URL(window.location.href);
    url.searchParams.set("initialTab", "integrations");
    return url.toString();
  })();

  const { data: catalogData, refetch: refetchConnectorCatalog } = useGetConnectorCatalogQuery({
    variables: { returnUrl } as never,
  });
  const { data: enabledConnectorsData } = useGetEnabledConnectorsQuery({
    variables: {
      filter: {
        status: ConnectorInstanceStatus.Active,
        role: userInfo.userRole,
      },
    },
  });
  const { data: userData, refetch: refetchUserConnections } = useGetUserConnectionsQuery({
    variables: {
      statuses: [
        UserExternalAuthStatus.Active,
        UserExternalAuthStatus.ReauthRequired,
        UserExternalAuthStatus.Error,
        UserExternalAuthStatus.Revoked,
      ],
    },
  });

  const allowedEnabledConnectors = (enabledConnectorsData?.getEnabledConnectors?.data ?? []).filter(isConnection);
  const allowedConnectorTypes = new Set(
    allowedEnabledConnectors
      .filter((connector) => connectorAllowsUserRole(connector.enabledRoles, userInfo.userRole))
      .map((connector) => connector.connectorType)
  );
  const availableConnectors = (catalogData?.getConnectorCatalog?.data ?? [])
    .filter(isConnection)
    .filter(isPersonalConnector)
    .filter((connector) => allowedConnectorTypes.has(connector.connectorType));

  const userConnections = (userData?.getUserConnections?.data ?? []).filter(isConnection);
  const connectedServices = userConnections
    .filter((connection) => allowedConnectorTypes.has(connection.connectorType))
    .map((connection) => ({
      ...connection,
      connectorDefinition: availableConnectors.find((c) => c.connectorType === connection.connectorType) || null,
    }));

  const connectionIssueSignature = connectedServices
    .filter((connection) => hasUnresolvedConnectionIssue(connection.status, connection.healthStatus))
    .map(
      (connection) =>
        `${connection.id}:${connection.status}:${connection.healthStatus}:${connection.lastHealthError ?? ""}`
    )
    .sort()
    .join("|");
  const connectionIssueCount = connectionIssueSignature
    ? connectedServices.filter((connection) => hasUnresolvedConnectionIssue(connection.status, connection.healthStatus))
        .length
    : 0;
  const visibleConnectionIssueCount =
    connectionIssueSignature && dismissedIssueSignature !== connectionIssueSignature ? connectionIssueCount : 0;

  useEffect(() => {
    if (!connectionIssueSignature && dismissedIssueSignature) {
      setDismissedIssueSignature(null);
    }
  }, [connectionIssueSignature, dismissedIssueSignature]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const connectorType = params.get("connectorType");
    const reconnectUserConnectionId = params.get("reconnectUserConnectionId");

    const connectionId = params.get("connectionId");
    if (!connectorType || !isConnectorType(connectorType)) {
      return;
    }
    const handleOAuthCallback = async () => {
      try {
        const [{ data: refreshedUserConnectionsData }] = await Promise.all([
          refetchUserConnections(),
          refetchConnectorCatalog(),
        ]);
        const refreshedConnections = (refreshedUserConnectionsData?.getUserConnections?.data ?? []).filter(
          isConnection
        );
        const completedConnection = connectionId
          ? refreshedConnections.find((connection) => connection.id === connectionId)
          : null;
        if (completedConnection) {
          showToast({
            message: `${connectorType} ${reconnectUserConnectionId ? "reconnected" : "connected"} successfully`,
            type: "success",
          });
        }
        clearOAuthQueryParams();
      } catch (error) {
        Sentry.captureException(error);
        showToast({
          message: `Unable to refresh ${connectorType} connection status`,
          type: "error",
        });
        clearOAuthQueryParams();
      }
    };
    handleOAuthCallback();
  }, [clearOAuthQueryParams, refetchConnectorCatalog, refetchUserConnections, showToast]);

  const handleConnect = async (connector: (typeof availableConnectors)[number]) => {
    try {
      setConnectingConnectorId(connector.id);
      const authUrl = connector.savedConfiguration?.authorizationUrl;
      if (!authUrl) {
        setConnectingConnectorId(null);
        showToast({
          message: `Unable to start connection for ${connector.connectorType}`,
          type: "error",
        });
        return;
      }

      if (Platform.OS === "web") {
        window.location.href = authUrl;
      } else {
        await handleOpenLink(authUrl);
        setConnectingConnectorId(null);
      }
    } catch (error) {
      setConnectingConnectorId(null);
      Sentry.captureException(error);
      showToast({
        message: `Unable to start connection for ${connector.connectorType}`,
        type: "error",
      });
    }
  };

  const handleReconnect = async (userConnectionId: string, connectorType: ConnectorType) => {
    try {
      if (typeof window === "undefined") {
        return;
      }
      setReconnectingConnectionId(userConnectionId);

      const reconnectReturnUrl = new URL(window.location.href);
      reconnectReturnUrl.searchParams.set("initialTab", "integrations");
      reconnectReturnUrl.searchParams.set("reconnectUserConnectionId", userConnectionId);
      reconnectReturnUrl.searchParams.set("connectorType", connectorType);

      const connectorCatalogVariables: Parameters<typeof refetchConnectorCatalog>[0] = {
        returnUrl: reconnectReturnUrl.toString(),
      };

      const { data } = await refetchConnectorCatalog(connectorCatalogVariables);

      const connector = (data?.getConnectorCatalog?.data ?? [])
        .filter(isConnection)
        .find((item) => item.connectorType === connectorType);

      const authUrl = connector?.savedConfiguration?.authorizationUrl;
      if (!authUrl) {
        setReconnectingConnectionId(null);
        showToast({
          message: `Unable to start reconnect for ${connectorType}`,
          type: "error",
        });
        return;
      }

      if (Platform.OS === "web") {
        window.location.href = authUrl;
      } else {
        await handleOpenLink(authUrl);
        setReconnectingConnectionId(null);
      }
    } catch (error) {
      setReconnectingConnectionId(null);
      Sentry.captureException(error);
      showToast({
        message: `Unable to start reconnect for ${connectorType}`,
        type: "error",
      });
    }
  };

  const handleConfirmDisconnect = async () => {
    if (!serviceToDisconnect) {
      return;
    }

    try {
      setIsDisconnecting(true);
      const { data } = await disconnectAccount({
        variables: {
          input: {
            userConnectionId: serviceToDisconnect.id,
          },
        },
      });

      if (data?.disconnectUserAccount?.success) {
        showToast({
          message: `${serviceToDisconnect.name} disconnected successfully`,
          type: "success",
        });
        await refetchUserConnections();
      } else {
        showToast({
          message: data?.disconnectUserAccount?.message || "Failed to disconnect account",
          type: "error",
        });
      }
    } catch (error) {
      Sentry.captureException(error);
      showToast({
        message: "An error occurred while disconnecting the account",
        type: "error",
      });
    } finally {
      setIsDisconnecting(false);
      setServiceToDisconnect(null);
    }
  };

  const getIntegrationStatus = (type: ConnectorType) => {
    const connection = userConnections.find((userConnection) => userConnection.connectorType === type);
    return {
      isConnected: Boolean(connection),
    };
  };

  return (
    <View>
      <View style={styles.headerCard}>
        <CustomText style={styles.headerTitle} text="Connected Services" useTranslationText={false} />
        <CustomSpacing size="xs" />
        <CustomText
          style={styles.headerSubtitle}
          text="Connect your personal accounts to access files and calendars from within Axio"
          useTranslationText={false}
        />
      </View>

      <CustomSpacing size="xl" />
      <View style={styles.connectedSection}>
        <ConnectorReauthenticationBanner
          issueCount={visibleConnectionIssueCount}
          onDismiss={connectionIssueSignature ? () => setDismissedIssueSignature(connectionIssueSignature) : undefined}
        />
      </View>
      {connectedServices.length === 0 && availableConnectors.length === 0 && (
        <View style={styles.connectedSection}>
          <CustomSpacing size="xl" />
          <CustomText text={"integrations_no_services_enabled_by_admin_yet"} useTranslationText={true} />
        </View>
      )}
      {connectedServices.length > 0 ? (
        <View style={styles.connectedSection}>
          <CustomSpacing size="xl" />

          <View style={styles.connectedList}>
            {connectedServices.map((userConnection) => (
              <ConnectedServiceAccordion
                key={userConnection.id}
                serviceName={userConnection.connectorDefinition?.name ?? userConnection.connectorType}
                accountLabel={userConnection.accountEmail ?? "No account email"}
                iconKey={userConnection.connectorDefinition?.icon}
                statusLabel={getStatusLabel(userConnection.status, userConnection.healthStatus)}
                statusTone={getConnectionStatusTone(userConnection.status, userConnection.healthStatus)}
                connectedValue={formatSubmittedDate(userConnection.created) || "-"}
                lastSyncedValue={formatSubmittedDate(userConnection.lastSyncAt) || "-"}
                permissions={
                  resolveConnectorPermissions(userConnection.connectorType, userConnection.permissions ?? []) ?? []
                }
                isLoading={reconnectingConnectionId === userConnection.id}
                onDisconnect={() =>
                  setServiceToDisconnect({
                    id: userConnection.id,
                    name: userConnection.connectorDefinition?.name ?? userConnection.connectorType,
                    email: userConnection.accountEmail ?? "",
                  })
                }
                onReconnect={
                  connectionNeedsReconnect(userConnection.status, userConnection.healthStatus)
                    ? () => handleReconnect(userConnection.id, userConnection.connectorType)
                    : undefined
                }
              />
            ))}
          </View>
        </View>
      ) : null}

      {connectedServices.length !== availableConnectors.length ? <View style={styles.separator} /> : null}

      {connectedServices.length !== availableConnectors.length ? (
        <View style={styles.connectSection}>
          <View style={styles.connectHeader}>
            <Plus size={18} color={colors.grey[900]} />
            <CustomSpacing size="xs" />
            <CustomText style={styles.connectTitle} text="Connect a Service" useTranslationText={false} />
          </View>

          <CustomSpacing size="l" />

          <View style={styles.grid}>
            {availableConnectors.map((connector) => {
              const { isConnected } = getIntegrationStatus(connector.connectorType);
              if (isConnected) {
                return null;
              }
              return (
                <ServiceConnectorCard
                  key={connector.id}
                  connector={connector}
                  onConnect={handleConnect}
                  size={32}
                  isLoading={connectingConnectorId === connector.id}
                />
              );
            })}
          </View>
        </View>
      ) : null}
      <DisconnectServiceModal
        visible={!!serviceToDisconnect}
        onClose={() => setServiceToDisconnect(null)}
        onConfirm={handleConfirmDisconnect}
        serviceName={serviceToDisconnect?.name ?? ""}
        accountEmail={serviceToDisconnect?.email ?? ""}
        isLoading={isDisconnecting}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  headerCard: {
    padding: 32,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: "#eaecf0",
    alignItems: "flex-start",
    backgroundColor: "#fff",
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "700",
    color: colors.grey[900],
  },
  headerSubtitle: {
    fontSize: 14,
    color: colors.grey[500],
    lineHeight: 20,
  },
  separator: {
    height: 1,
    backgroundColor: "#f2f4f7",
    marginVertical: 22,
  },
  connectSection: {
    paddingHorizontal: 8,
  },
  connectedSection: {
    paddingHorizontal: 8,
  },
  connectedList: {
    gap: 16,
  },
  connectHeader: {
    flexDirection: "row",
    alignItems: "center",
  },
  connectTitle: {
    paddingLeft: 8,
    fontSize: 16,
    fontWeight: "600",
    color: colors.grey[900],
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
  },
});
